import React, { useState } from 'react';
import { Heart, Users, Clock, Home as HomeIcon, Phone, Mail, MapPin, Check, Menu, X, Facebook, Instagram, Linkedin, CalendarCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { mockTestimonials } from '../mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [bookingData, setBookingData] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_date: '',
    preferred_time: '',
    note: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success(response.data.message || 'Thank you! We\'ll contact you within 24 hours.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBookingChange = (e) => {
    setBookingData({ ...bookingData, [e.target.name]: e.target.value });
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setBookingSubmitting(true);
    try {
      const response = await axios.post(`${API}/booking`, bookingData);
      toast.success(response.data.message || 'Thank you! Your consultation request has been received.');
      setBookingData({ name: '', email: '', phone: '', preferred_date: '', preferred_time: '', note: '' });
      setBookingOpen(false);
    } catch (error) {
      console.error('Booking form error:', error);
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setBookingSubmitting(false);
    }
  };

  const openBooking = () => {
    setMobileMenuOpen(false);
    setBookingOpen(true);
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const services = [
    {
      icon: Heart,
      title: 'Personal Care',
      description: 'Compassionate assistance with daily activities including bathing, grooming, dressing, and mobility support.'
    },
    {
      icon: Clock,
      title: 'Medication Reminders',
      description: 'Timely medication management and health monitoring to ensure safety and wellbeing.'
    },
    {
      icon: Users,
      title: 'Companionship',
      description: 'Meaningful social interaction, conversation, and emotional support to combat loneliness.'
    },
    {
      icon: HomeIcon,
      title: 'Meal Preparation',
      description: 'Nutritious meal planning and preparation tailored to dietary needs and preferences.'
    },
    {
      icon: Heart,
      title: 'Light Housekeeping',
      description: 'Maintaining a clean, safe, and comfortable living environment.'
    },
    {
      icon: Users,
      title: 'Mobility Assistance',
      description: 'Safe transfer support and assistance with walking aids to maintain independence.'
    }
  ];

  const serviceAreas = ['Central Florida', 'Orlando', 'Lake Nona', 'Saint Cloud', 'Kissimmee', 'Harmony', 'Poinciana'];

  return (
    <div className="min-h-screen bg-white">
      {/* Booking Dialog */}
      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent className="sm:max-w-md" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl text-teal-700 flex items-center gap-2">
              <CalendarCheck size={24} /> Book a Free Consultation
            </DialogTitle>
            <DialogDescription>
              Choose a preferred date and time, and we'll confirm your appointment within 24 hours.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBookingSubmit} className="space-y-4 mt-2">
            <div className="space-y-1">
              <Label htmlFor="booking-name">Full Name *</Label>
              <Input
                id="booking-name"
                data-testid="booking-name-input"
                name="name"
                value={bookingData.name}
                onChange={handleBookingChange}
                placeholder="Your name"
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="booking-email">Email *</Label>
              <Input
                id="booking-email"
                data-testid="booking-email-input"
                type="email"
                name="email"
                value={bookingData.email}
                onChange={handleBookingChange}
                placeholder="your@email.com"
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="booking-phone">Phone Number *</Label>
              <Input
                id="booking-phone"
                data-testid="booking-phone-input"
                name="phone"
                value={bookingData.phone}
                onChange={handleBookingChange}
                placeholder="(555) 123-4567"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="booking-date">Preferred Date *</Label>
                <Input
                  id="booking-date"
                  data-testid="booking-date-input"
                  type="date"
                  name="preferred_date"
                  value={bookingData.preferred_date}
                  onChange={handleBookingChange}
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="booking-time">Preferred Time *</Label>
                <Input
                  id="booking-time"
                  data-testid="booking-time-input"
                  type="time"
                  name="preferred_time"
                  value={bookingData.preferred_time}
                  onChange={handleBookingChange}
                  required
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="booking-note">Tell us about your care needs (optional)</Label>
              <Textarea
                id="booking-note"
                data-testid="booking-note-input"
                name="note"
                value={bookingData.note}
                onChange={handleBookingChange}
                placeholder="Any details that will help us prepare..."
                rows={3}
              />
            </div>
            <Button
              type="submit"
              data-testid="booking-submit-button"
              disabled={bookingSubmitting}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white transition-all"
            >
              {bookingSubmitting ? 'Submitting...' : 'Request Consultation'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo.png" 
                alt="Graceful Hands Private Care Logo" 
                className="h-20 w-20"
              />
              <div className="flex flex-col">
                <span className="text-xl font-bold text-teal-700">Graceful Hands</span>
                <span className="text-sm text-teal-600">Private Care</span>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium">About</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium">Services</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium">Testimonials</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium">Contact</button>
              <Button
                data-testid="nav-book-consultation-button"
                onClick={openBooking}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-5 transition-all"
              >
                <CalendarCheck className="mr-2" size={18} />
                Book a Free Consultation
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-gray-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left py-2 text-gray-700 hover:text-teal-600 font-medium">Home</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left py-2 text-gray-700 hover:text-teal-600 font-medium">About</button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left py-2 text-gray-700 hover:text-teal-600 font-medium">Services</button>
              <button onClick={() => scrollToSection('testimonials')} className="block w-full text-left py-2 text-gray-700 hover:text-teal-600 font-medium">Testimonials</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left py-2 text-gray-700 hover:text-teal-600 font-medium">Contact</button>
              <Button
                data-testid="mobile-nav-book-consultation-button"
                onClick={openBooking}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full mt-2 transition-all"
              >
                <CalendarCheck className="mr-2" size={18} />
                Book a Free Consultation
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Compassionate <span className="text-teal-600">Private Care</span> for Your Loved Ones
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                Professional CNA services providing dignified, personalized care in the comfort of home. Serving Central Florida with grace and expertise.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  data-testid="hero-book-consultation-button"
                  onClick={openBooking} 
                  className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 text-lg transition-all"
                >
                  <CalendarCheck className="mr-2" size={20} />
                  Book a Free Consultation
                </Button>
                <Button 
                  onClick={() => scrollToSection('services')} 
                  variant="outline" 
                  className="border-teal-600 text-teal-600 hover:bg-teal-50 px-8 py-6 text-lg transition-all"
                >
                  Our Services
                </Button>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <MapPin className="text-teal-600" size={20} />
                <span className="font-medium">Serving {serviceAreas.join(', ')}</span>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="/hero-collage.png" 
                  alt="Graceful Hands Private Care - More Than Care, It's Family" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border-4 border-teal-100">
                <div className="text-center">
                  <div className="text-3xl font-bold text-teal-600">10+</div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-teal-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="/about-books.png" 
                alt="Graceful Hands Private Care - Compassion, Commitment, Care" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                About Graceful Hands Private Care
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                At Graceful Hands Private Care, we believe that everyone deserves to age with dignity and maintain their independence in the comfort of their own home. As certified care professionals, we bring professional expertise combined with genuine compassion to every client interaction.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our personalized approach ensures that each care plan is tailored to your loved one's unique needs, preferences, and lifestyle. We don't just provide care—we build meaningful relationships based on trust, respect, and understanding.
              </p>
              <div className="grid grid-cols-2 gap-4 pt-4">
                <div className="flex items-start space-x-3">
                  <Check className="text-teal-600 flex-shrink-0" size={24} />
                  <span className="text-gray-700 font-medium">Certified CNA Professional</span>
                </div>
                <div className="flex items-start space-x-3">
                  <Check className="text-teal-600 flex-shrink-0" size={24} />
                  <span className="text-gray-700 font-medium">Certified HHA Professional</span>
                </div>
                <div className="flex items-start space-x-3">
                  <Check className="text-teal-600 flex-shrink-0" size={24} />
                  <span className="text-gray-700 font-medium">Personalized Care Plans</span>
                </div>
                <div className="flex items-start space-x-3">
                  <Check className="text-teal-600 flex-shrink-0" size={24} />
                  <span className="text-gray-700 font-medium">24/7 Availability</span>
                </div>
                <div className="flex items-start space-x-3">
                  <Check className="text-teal-600 flex-shrink-0" size={24} />
                  <span className="text-gray-700 font-medium">Trusted by Families</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Care Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional support tailored to your loved one's needs, ensuring comfort, safety, and quality of life.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="border-2 hover:border-teal-300 transition-all hover:shadow-xl">
                  <CardHeader>
                    <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                      <IconComponent className="text-teal-600" size={28} />
                    </div>
                    <CardTitle className="text-xl text-gray-900">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Families Say
            </h2>
            <p className="text-lg text-gray-600">
              Trusted by families across Central Florida
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {mockTestimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white border-none shadow-lg">
                <CardHeader>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                      <span className="text-teal-700 font-bold text-lg">{testimonial.name.charAt(0)}</span>
                    </div>
                    <div>
                      <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                      <CardDescription>{testimonial.location}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 italic leading-relaxed">"{testimonial.text}"</p>
                  <div className="flex mt-4 text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <span key={i}>★</span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Get in Touch
                </h2>
                <p className="text-lg text-gray-600">
                  Ready to discuss care options for your loved one? We're here to help.
                </p>
              </div>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="text-teal-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                    <a href="tel:689-216-5291" className="text-gray-600 hover:text-teal-600 transition-colors">689-216-5291</a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="text-teal-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <a href="mailto:info@gracefulhandsprivatecare.com" className="text-gray-600 hover:text-teal-600 transition-colors break-all">info@gracefulhandsprivatecare.com</a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-teal-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Service Areas</h3>
                    <p className="text-gray-600">{serviceAreas.join(', ')}</p>
                  </div>
                </div>
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Follow Us</h3>
                  <div className="flex space-x-4">
                    <a 
                      href="https://www.facebook.com/gracefulhandspc" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center hover:bg-teal-600 hover:text-white transition-all"
                    >
                      <Facebook size={24} />
                    </a>
                    <a 
                      href="https://www.instagram.com/gracefulhandspc" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center hover:bg-teal-600 hover:text-white transition-all"
                    >
                      <Instagram size={24} />
                    </a>
                    <a 
                      href="https://www.tiktok.com/@gracefulhandspc" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center hover:bg-teal-600 hover:text-white transition-all"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                      </svg>
                    </a>
                    <a 
                      href="https://www.linkedin.com/company/gracefulhandspc" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center hover:bg-teal-600 hover:text-white transition-all"
                    >
                      <Linkedin size={24} />
                    </a>
                  </div>
                  <p className="text-sm text-gray-500 mt-3">@gracefulhandspc</p>
                </div>
              </div>
            </div>
            <Card className="border-2 shadow-xl">
              <CardHeader>
                <CardTitle>Request Information</CardTitle>
                <CardDescription>Fill out the form below and we'll contact you within 24 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <Input 
                      type="text" 
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name" 
                      required 
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <Input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com" 
                      required 
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                    <Input 
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567" 
                      required 
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                    <Textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your care needs..." 
                      rows={4}
                      className="w-full"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6 text-lg transition-all">
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="/logo.png" 
                  alt="Graceful Hands Logo" 
                  className="h-16 w-16"
                />
                <div className="flex flex-col">
                  <span className="text-lg font-bold">Graceful Hands</span>
                  <span className="text-sm text-gray-400">Private Care</span>
                </div>
              </div>
              <p className="text-gray-400 leading-relaxed mb-4">
                Providing compassionate, professional CNA services to families across Central Florida.
              </p>
              <div className="flex space-x-3">
                <a 
                  href="https://www.facebook.com/gracefulhandspc" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors"
                >
                  <Facebook size={20} />
                </a>
                <a 
                  href="https://www.instagram.com/gracefulhandspc" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors"
                >
                  <Instagram size={20} />
                </a>
                <a 
                  href="https://www.tiktok.com/@gracefulhandspc" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.linkedin.com/company/gracefulhandspc" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors"
                >
                  <Linkedin size={20} />
                </a>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-white transition-colors">About Us</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-white transition-colors">Services</button></li>
                <li><button onClick={() => scrollToSection('testimonials')} className="text-gray-400 hover:text-white transition-colors">Testimonials</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-white transition-colors">Contact</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-4">Service Areas</h3>
              <ul className="space-y-2">
                {serviceAreas.map((area, index) => (
                  <li key={index} className="text-gray-400">{area}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Graceful Hands Private Care. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};